import {
    Box,
    Card,
    CardActionArea,
    Chip,
    Dialog,
    Grid,
    IconButton,
    Typography,
} from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { PosContext } from "../../shared/PosContext";

import {
    Add,
    FoodBankRounded,
    ManageAccounts,
    OpenInNewRounded,
    Receipt,
    SwapHoriz,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../AppContext";
import { isRoleService } from "../../frappeConfig";
import WidgetsDialogs from "../../pages/Widgets/WidgetsDialogs";
import CustomerManagementDialog from "../../pages/PosPage/components/CustomerManagementDialog";
import CustomerDialog from "../../pages/PosPage/components/CustomerDialog/CustomerDialog";
import { dateFormat, dateTimeFormat } from "../../utils/dateUtils";
import { getOrderShortName } from "../../utils/floorUtils";
import CustomerCreationWizard from "../Customer/CustomerCreationWizard";
import SimpleWidgetsHeader from "../SimpleWidgetsHeader/SimpleWidgetsHeader";
import SimpleWidgetsInput from "../SimpleWidgetsInput/SimpleWidgetsInput";
import { useStyles } from "./styles";
import { useUpdateDoctypeData } from "../../shared/hooks/useDocTypesData";

const parseFrappeDatetime = (value) => {
    if (!value) return null;
    // Common Frappe format: "YYYY-MM-DD HH:mm:ss"
    const asString = String(value);
    const isoLike = asString.includes(" ") ? asString.replace(" ", "T") : asString;
    const t = Date.parse(isoLike);
    return Number.isNaN(t) ? null : t;
};

const formatSince = (fromMs, nowMs) => {
    if (!fromMs || !nowMs || nowMs < fromMs) return "";
    const diffMin = Math.floor((nowMs - fromMs) / 60000);
    if (diffMin < 60) return `${diffMin}m`;
    const h = Math.floor(diffMin / 60);
    const m = diffMin % 60;
    return `${h}h${m ? ` ${m}m` : ""}`;
};

const TakeawayOrdersList = ({ onClose, title, onOpenCustomerBrowser }) => {
    const classes = useStyles();
    const { takeawayTable, customers, refetchCustomers, refetchTablesData } =
        useContext(PosContext);

    const { userInfo, isTabsPos, addresses } = useContext(AppContext);

    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [createCustomerDialogOpen, setCreateCustomerDialogOpen] =
        useState(false);

    const [selectedOrder, setSelectedOrder] = useState(null);
    const [widgetsDialogsOpen, setWidgetsDialogsOpen] = useState(false);

    const updateDoctype = useUpdateDoctypeData();
    const [customerMgmtOpen, setCustomerMgmtOpen] = useState(false);
    const [customerMgmtTab, setCustomerMgmtTab] = useState(0);
    const [customerMgmtOrder, setCustomerMgmtOrder] = useState(null);
    const [customerMgmtCustomer, setCustomerMgmtCustomer] = useState(null);
    const [customerMgmtAddress, setCustomerMgmtAddress] = useState(null);

    const [directCustomerDialogOpen, setDirectCustomerDialogOpen] =
        useState(false);
    const [directCustomerOrder, setDirectCustomerOrder] = useState(null);

    const [nowMs, setNowMs] = useState(Date.now());

    const navigate = useNavigate();

    const [searchText, setSearchText] = useState("");

    // Update "since" timer without refreshing the page
    useEffect(() => {
        const id = setInterval(() => setNowMs(Date.now()), 60 * 1000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        setFilteredOrders(orders);
    }, []);

    useEffect(() => {
        if (customers && takeawayTable) {
            const tmpOrders = [];
            const findCustomer = (customer) => {
                const cust = customers.find((i) => i.name === customer);
                return cust;
            };

            Object.values(takeawayTable.orders).forEach((order) => {
                tmpOrders.push({
                    order: order,
                    customer: findCustomer(order.customer),
                });
            });
            setOrders(tmpOrders);
            setFilteredOrders(tmpOrders);
        }
    }, [takeawayTable, customers]);

    useEffect(() => {
        if (!searchText && orders.length > 0) {
            setFilteredOrders(orders);
        } else {
            const tmpOrders = [];
            orders.forEach((order) => {
                const searchFields =
                    order.customer.name +
                    order.customer.mobile_no +
                    getOrderShortName(order.order.name);
                if (
                    searchFields
                        .toLowerCase()
                        .includes(searchText.toLowerCase())
                ) {
                    tmpOrders.push(order);
                }
                setFilteredOrders(tmpOrders);
            });
        }
    }, [searchText]);

    const onCloseCreateCustomer = () => {
        setCreateCustomerDialogOpen(false);
    };

    const handleCustomerCreated = (customerData) => {
        refetchCustomers();
        setSearchText(customerData.data.name);
    };

    const newTakeawayOrder = isRoleService(userInfo.role_profile_name)
        ? null
        : {
              callback: () => {
                  navigate("/pos/takeaway");
              },
              Icon: Add,
              label: "New order",
          };

    const headerActions = useMemo(() => {
        const list = [];
        if (newTakeawayOrder) list.push(newTakeawayOrder);
        if (onOpenCustomerBrowser) {
            list.push({
                callback: onOpenCustomerBrowser,
                Icon: ManageAccounts,
                label: "Customers",
            });
        }
        return list;
    }, [newTakeawayOrder, onOpenCustomerBrowser]);

    const handleCloseDialog = () => {
        setWidgetsDialogsOpen(false);
    };

    const handleListAction = (order) => {
        setSelectedOrder(order.order);
        setWidgetsDialogsOpen(true);
    };

    const handleMoveOrderToTableAction = (order) => {
        const moveTakeAwayOrderEvent = new CustomEvent("requestMoveToTable", {
            detail: { table: takeawayTable },
        });
        document.dispatchEvent(moveTakeAwayOrderEvent);
    };

    const openCustomerMgmt = (order, tabIdx = 0) => {
        setCustomerMgmtTab(tabIdx);
        setCustomerMgmtOrder(order?.order || null);
        setCustomerMgmtCustomer(order?.order?.customer || null);
        setCustomerMgmtAddress(order?.order?.address || null);
        setCustomerMgmtOpen(true);
    };

    const closeCustomerMgmt = () => setCustomerMgmtOpen(false);
    const closeDirectCustomerDialog = () => setDirectCustomerDialogOpen(false);

    const applyCustomerChange = (customer, address) => {
        if (!customerMgmtOrder?.name) return;
        const payload = { customer };
        if (address) payload.address = address;

        // Close immediately for better UX; refresh on success so cards update.
        setCustomerMgmtOpen(false);

        updateDoctype.mutate(
            {
                doctype: "Table Order",
                name: customerMgmtOrder.name,
                formData: JSON.stringify(payload),
            },
            {
                onSuccess: () => {
                    refetchTablesData?.();
                    refetchCustomers?.();
                    setCustomerMgmtOpen(false);
                },
            }
        );
    };

    const applyDirectCustomerChange = (customer, address) => {
        if (!directCustomerOrder?.name) return;
        const payload = { customer };
        if (address) payload.address = address;

        setDirectCustomerDialogOpen(false);

        updateDoctype.mutate(
            {
                doctype: "Table Order",
                name: directCustomerOrder.name,
                formData: JSON.stringify(payload),
            },
            {
                onSuccess: () => {
                    refetchTablesData?.();
                    refetchCustomers?.();
                    setDirectCustomerDialogOpen(false);
                },
            }
        );
    };

    const getAddressDocForOrder = (orderObj, customerObj) => {
        if (!Array.isArray(addresses)) return null;
        const addressName = orderObj?.address;
        if (addressName) {
            return addresses.find((a) => a.name === addressName) || null;
        }
        const customerName = customerObj?.name;
        if (!customerName) return null;
        return (
            addresses.find((a) => a.address_title == customerName) || null
        );
    };

    const getPhoneFromAddressDoc = (addressDoc) => {
        if (!addressDoc) return "";
        return (
            addressDoc.phone ||
            addressDoc.phone_no ||
            addressDoc.mobile_no ||
            addressDoc.mobile ||
            ""
        );
    };

    const getCustomerAddress = (orderObj, customerObj) => {
        const doc = getAddressDocForOrder(orderObj, customerObj);
        const address1 = doc?.address_line1 || "";
        const address2 = doc?.address_line2 || "";
        return `${address1 ? `${address1}` : ""} ${
            address2 ? `- ${address2}` : ""
        }`.trim();
    };

    return (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
            }}
        >
            <SimpleWidgetsHeader
                onClose={onClose}
                Icon={FoodBankRounded}
                title={title || "Takeaway Orders"}
                actions={headerActions}
            />

            <SimpleWidgetsInput
                isSearch
                placeholder="Search by name or phone #"
                value={searchText}
                onChange={setSearchText}
            />

            <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", minHeight: 0 }}>
                <Grid container spacing={2}>
                    {takeawayTable &&
                        filteredOrders.map((order) => {
                            const orderName = order.order.name;
                            const customer = order.customer || {};
                            const address = getCustomerAddress(order.order, customer);
                            const addressDoc = getAddressDocForOrder(
                                order.order,
                                customer
                            );
                            const effectivePhone =
                                getPhoneFromAddressDoc(addressDoc) ||
                                customer.mobile_no ||
                                "";
                            const created = order.order.creation || "";
                            const createdMs = parseFrappeDatetime(created);
                            const since = formatSince(createdMs, nowMs);
                            const createdLabel = created
                                ? `${dateFormat(created)
                                      .split("/")
                                      .slice(0, 2)
                                      .join("/")} - ${dateTimeFormat(
                                      created.split(" ")[1] || ""
                                  )}`
                                : "";

                            // Preview items (best-effort; depends on payload shape)
                            const entryItems =
                                order.order.entry_items ||
                                order.order.items ||
                                [];
                            const preview = Array.isArray(entryItems)
                                ? entryItems.slice(0, 3)
                                : [];
                            const totalQty = Array.isArray(entryItems)
                                ? entryItems.reduce(
                                      (sum, i) => sum + (i.qty || 0),
                                      0
                                  )
                                : null;

                            return (
                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    lg={3}
                                    key={`Takeaway_Orders_${orderName}`}
                                >
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            borderRadius: 2,
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                        }}
                                    >
                                        <CardActionArea
                                            onClick={() =>
                                                navigate(
                                                    `/pos/takeaway/${orderName}`
                                                )
                                            }
                                            sx={{ p: 2, flex: 1 }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    alignItems: "flex-start",
                                                    gap: 1,
                                                }}
                                            >
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontWeight: 900,
                                                            color: "#e14954",
                                                            lineHeight: 1.2,
                                                        }}
                                                    >
                                                        #{getOrderShortName(orderName)}{" "}
                                                        {since ? (
                                                            <Typography
                                                                component="span"
                                                                variant="caption"
                                                                sx={{
                                                                    color: "#6E7187",
                                                                    fontWeight: 800,
                                                                }}
                                                            >
                                                                ({since})
                                                            </Typography>
                                                        ) : null}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: "#271D1C",
                                                            fontWeight: 700,
                                                            overflow: "hidden",
                                                            textOverflow:
                                                                "ellipsis",
                                                            whiteSpace:
                                                                "nowrap",
                                                        }}
                                                    >
                                                        {customer.name ||
                                                            "—"}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: "#6E7187",
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {effectivePhone}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection:
                                                            "column",
                                                        alignItems: "flex-end",
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Chip
                                                        size="small"
                                                        label={`${order.order.amount}₪`}
                                                        sx={{
                                                            fontWeight: 900,
                                                            bgcolor:
                                                                "rgba(225, 73, 84, 0.12)",
                                                            color: "#e14954",
                                                        }}
                                                    />
                                                    {totalQty != null ? (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: "#6E7187",
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {totalQty} items
                                                        </Typography>
                                                    ) : null}
                                                </Box>
                                            </Box>

                                            {address ? (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: "block",
                                                        mt: 1,
                                                        color: "#6E7187",
                                                        fontWeight: 600,
                                                        overflow: "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {address}
                                                </Typography>
                                            ) : null}

                                            {preview.length > 0 ? (
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: "#6E7187",
                                                            fontWeight: 800,
                                                            textTransform:
                                                                "uppercase",
                                                        }}
                                                    >
                                                        Items
                                                    </Typography>
                                                    <Box sx={{ mt: 0.5 }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 700,
                                                                color: "#271D1C",
                                                                overflow: "hidden",
                                                                textOverflow:
                                                                    "ellipsis",
                                                                whiteSpace:
                                                                    "nowrap",
                                                            }}
                                                        >
                                                            {preview
                                                                .map((i) => {
                                                                    const label =
                                                                        i.item_code ||
                                                                        i.item ||
                                                                        i.item_name ||
                                                                        "";
                                                                    return `${i.qty || 0}× ${label}`;
                                                                })
                                                                .join(", ")}
                                                            {Array.isArray(
                                                                entryItems
                                                            ) &&
                                                            entryItems.length >
                                                                preview.length
                                                                ? `, +${
                                                                      entryItems.length -
                                                                      preview.length
                                                                  } more`
                                                                : ""}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ) : null}

                                            {createdLabel ? (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: "block",
                                                        mt: 1,
                                                        color: "#6E7187",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {createdLabel}
                                                </Typography>
                                            ) : null}
                                        </CardActionArea>

                                        <Box
                                            sx={{
                                                p: 1,
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                gap: 1.5,
                                            }}
                                        >
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const rawCustomer =
                                                        order?.order?.customer;
                                                    if (
                                                        !rawCustomer ||
                                                        rawCustomer ===
                                                            "takeaway-customer"
                                                    ) {
                                                        setDirectCustomerOrder(
                                                            order?.order || null
                                                        );
                                                        setDirectCustomerDialogOpen(
                                                            true
                                                        );
                                                        return;
                                                    }
                                                    openCustomerMgmt(order, 0);
                                                }}
                                                size="small"
                                            >
                                                <ManageAccounts fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleListAction(order);
                                                }}
                                                size="small"
                                            >
                                                <Receipt fontSize="small" />
                                            </IconButton>
                                            {!isTabsPos && (
                                                <IconButton
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleMoveOrderToTableAction(
                                                            order
                                                        );
                                                    }}
                                                    size="small"
                                                >
                                                    <SwapHoriz fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Card>
                                </Grid>
                            );
                        })}
                </Grid>
            </Box>

            <Dialog maxWidth="xl" open={createCustomerDialogOpen}>
                <CustomerCreationWizard
                    onClose={onCloseCreateCustomer}
                    onConfirm={handleCustomerCreated}
                />
            </Dialog>

            {/* Payment Dialog */}
            <WidgetsDialogs
                open={widgetsDialogsOpen}
                onClose={handleCloseDialog}
                order={selectedOrder}
                table={takeawayTable}
                isTakeaway={true}
                onInvoiceClose={handleCloseDialog}
            ></WidgetsDialogs>

            <CustomerManagementDialog
                open={customerMgmtOpen}
                onClose={closeCustomerMgmt}
                initialTab={customerMgmtTab}
                selectedCustomer={customerMgmtCustomer}
                selectedAddress={customerMgmtAddress}
                selectedOrder={customerMgmtOrder}
                isTakeawayOverride={true}
                onApplyCustomerChange={applyCustomerChange}
            />

            <CustomerDialog
                dialogOpen={directCustomerDialogOpen}
                onCancel={closeDirectCustomerDialog}
                onConfirm={applyDirectCustomerChange}
                customerSelected={applyDirectCustomerChange}
                selectedCustomer={""}
                isTakeawayOverride={true}
            />
        </Box>
    );
};

export default TakeawayOrdersList;
